import type {
  EcomOrderAccounting,
  EcomOrderEInvoice,
  EcomOrderPacking,
  EcomOrderStockIssue,
} from '@/api/ecommerce/orders';
import type { OrderIssuedEInvoice } from '@/mocks/orders';
import { ROUTES } from '@/constants/routes';

export interface OrderAccountingDocumentSource {
  accounting?: EcomOrderAccounting | null;
  packing?: EcomOrderPacking;
  stock_issue?: EcomOrderStockIssue;
  e_invoice?: EcomOrderEInvoice | null;
  e_invoices?: OrderIssuedEInvoice[];
}

export type OrderAccountingDocumentType =
  | 'sales_invoice'
  | 'goods_issue'
  | 'stock_transfer'
  | 'e_invoice'
  | 'cash_receipt';

export interface OrderAccountingDocument {
  type: OrderAccountingDocumentType;
  label: string;
  doc_id: string;
  code: string;
  href: string;
}

/** Display order and detail routes shared with the order accounting card. */
export const ORDER_ACCOUNTING_DOCUMENT_TYPES: readonly OrderAccountingDocumentType[] = [
  'sales_invoice', 'goods_issue', 'stock_transfer', 'e_invoice', 'cash_receipt',
];

export const ORDER_ACCOUNTING_DOCUMENT_META: Record<
  OrderAccountingDocumentType,
  { label: string; href: (id: string) => string }
> = {
  sales_invoice: { label: 'CT bán hàng', href: ROUTES.accounting.salesDocumentDetail },
  goods_issue: { label: 'Phiếu xuất kho', href: ROUTES.inventory.issueDetail },
  stock_transfer: { label: 'Phiếu chuyển kho', href: ROUTES.inventory.transferDetail },
  e_invoice: { label: 'Hoá đơn GTGT', href: (id) => `${ROUTES.accounting.accountingEInvoices}/${id}` },
  cash_receipt: { label: 'Phiếu thu', href: ROUTES.accounting.receiptEdit },
};

export function resolveOrderAccountingDocuments(
  order: OrderAccountingDocumentSource,
): OrderAccountingDocument[] {
  const documents: OrderAccountingDocument[] = [];
  const seen = new Set<string>();

  const add = (type: OrderAccountingDocumentType, docId?: string | null, code?: string | null): void => {
    const id = docId?.trim();
    const displayCode = code?.trim();
    if (!id || !displayCode) return;
    const key = `${type}:${id}`;
    if (seen.has(key)) return;
    seen.add(key);
    const { label, href } = ORDER_ACCOUNTING_DOCUMENT_META[type];
    documents.push({ type, label, doc_id: id, code: displayCode, href: href(id) });
  };

  for (const ref of order.accounting?.documents ?? []) {
    if (ref.type === 'journal_entry') continue;
    add(ref.type, ref.doc_id, ref.code);
  }

  add('sales_invoice', order.packing?.accounting?.sales_invoice_id, order.packing?.accounting?.invoice_number);
  add('goods_issue', order.stock_issue?.goods_issue_id, order.stock_issue?.document_number);
  add('stock_transfer', order.packing?.transfer?.stock_transfer_id, order.packing?.transfer?.transfer_code);
  add('e_invoice', order.e_invoice?.invoice_id,
    order.e_invoice?.invoice_number?.trim() || order.e_invoice?.internal_invoice_number);
  for (const invoice of order.e_invoices ?? []) {
    const id = invoice.id?.trim();
    const code = invoice.invoice_number?.trim();
    if (!id || !code) continue;
    const existing = documents.find((doc) => doc.type === 'e_invoice' && doc.doc_id === id);
    if (existing) existing.code = code;
    else add('e_invoice', id, code);
  }

  const rank = (type: OrderAccountingDocumentType): number => ORDER_ACCOUNTING_DOCUMENT_TYPES.indexOf(type);
  return documents.sort((a, b) => rank(a.type) - rank(b.type));
}
